/* eslint-disable camelcase */
import { Grid, InputLabel } from '@mui/material';
import { Language } from '@nx-smartmonkey/shared/domain';
import { validatePhoneNumber } from '@nx-smartmonkey/shared/helpers';
import {
  Checkbox,
  HSpacer,
  RoutalAvatarManager,
  RoutalInputPhone,
  RoutalInputPhoneValue,
  RoutalPalette,
  RoutalSelect,
  VSpacer,
} from '@nx-smartmonkey/ui';
import { isEqual } from 'lodash';
import { useEffect, useState } from 'react';

import styled from 'styled-components';
import WarningIcon from '../../../assets/svg/WarningIcon';
import { HeaderContent } from '../../../components/HeaderContent';
import { UserNotifications, UserTwoFactor } from '../../../domain/user/User';
import { MAX_AVATAR_IMAGE_SIZE, UPLOAD_TYPES, generateAttachmentName } from '../../../helpers/Images';
import { intl } from '../../../helpers/IntGlobalProvider';
import useCreateUserAvatar from '../../../hooks/user/useCreateUserAvatar';
import useDeleteUserAvatar from '../../../hooks/user/useDeleteUserAvatar';
import { useResetUserPassword } from '../../../hooks/user/useResetUserPassword';
import { useUpdateUser } from '../../../hooks/user/useUpdateUser';
import Button from '../../../react-components/Button';
import InputText from '../../../react-components/InputText';
import Text from '../../../react-components/Text';
import { getUploadAsBase64 } from '../../../services/requests';
import { ValidatePhoneDialog } from './ValidatePhoneDialog';

const BoxContainer = styled(Grid)`
  border: 1px solid ${RoutalPalette.neutral10};
  border-radius: 10px;
  padding: 20px;
  max-width: 750px;
  min-width: max-content;
`;

type AccountProfileProps = {
  isDirtyState: (...args: any[]) => any;
};

export const AccountProfile = ({ isDirtyState }: AccountProfileProps) => {
  const { run: getResetPassword } = useResetUserPassword();
  const { isLoading, run: editUserProfile, data: user } = useUpdateUser();
  const { run: uploadAvatar } = useCreateUserAvatar({ user });
  const { run: removeAvatar } = useDeleteUserAvatar({ user });

  const [firstName, setFirstName] = useState<string | undefined>(``);
  const [lastName, setLastName] = useState<string | undefined>(``);
  const [email, setEmail] = useState(``);
  const [phone, setPhone] = useState<string>(``);
  const [twoFactor, setTwoFactor] = useState<UserTwoFactor>({ email_enabled: false, sms_enabled: false });
  const [language, setLanguage] = useState<Language>(Language.create(`es`));
  const [notifications, setNotifications] = useState<UserNotifications>({
    analytics_email: true,
  });

  const [validatePhoneDialogOptions, setValidatePhoneDialogOptions] = useState<
    | {
        open: boolean;
        fromTwoFactor: boolean;
      }
    | undefined
  >(undefined);

  const [dirtyState, setDirtyState] = useState(false);
  useEffect(() => {
    if (user) {
      setFirstName(user.first_name);
      setLastName(user.last_name);
      setEmail(user.email);
      setPhone(user.phone ?? ``);
      setLanguage(user.language);
      setNotifications(user.notifications);
      setTwoFactor({
        email_enabled: user.two_factor.email_enabled,
        sms_enabled: user.two_factor.sms_enabled,
      });
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      if (
        !isEqual(firstName, user.first_name) ||
        !isEqual(lastName, user.last_name) ||
        (!isEqual(phone, user.phone) && (validatePhoneNumber(phone) || phone === ``)) ||
        !isEqual(language.value, user.language.value) ||
        !isEqual(notifications, user.notifications) ||
        !isEqual(twoFactor, user.two_factor)
      ) {
        setDirtyState(true);
      } else {
        setDirtyState(false);
      }
    }
  }, [user, firstName, lastName, phone, language, notifications, twoFactor]);

  useEffect(() => {
    isDirtyState(dirtyState);
  }, [dirtyState, isDirtyState]);

  const handleEditUser = () => {
    editUserProfile({
      user: user!,
      firstName,
      lastName,
      phone,
      language,
      notifications,
      twoFactor,
    });
  };

  const handleResetPassword = async () => {
    if (user?.email) {
      getResetPassword({ email: user.email });
    }
  };

  const getLanguageIntlId = (language: Language) => {
    switch (language.value) {
      case `es`:
        return `settings.account.profile.es`;
      case `ca`:
        return `settings.account.profile.ca`;
      case `pt`:
        return `settings.account.profile.pt`;
      case `de`:
        return `settings.account.profile.de`;
      case `fr`:
        return `settings.account.profile.fr`;
      default:
        return `settings.account.profile.en`;
    }
  };

  const getCountryCode = (language: Language) => {
    switch (language.value) {
      case `en`:
        return `US`;
      case `pt`:
        return `PT`;
      case `de`:
        return `de`;
      case `fr`:
        return `fr`;
      default:
        return `ES`;
    }
  };

  if (!user) return null;

  return (
    <>
      <Grid
        container
        direction="column"
        wrap="nowrap"
        style={{ height: `100%`, backgroundColor: `white`, overflowY: `auto` }}
      >
        <HeaderContent
          titleIntlId="settings.account.profile.title"
          // article={{
          //   intlId: `projects.settings.fields.constraints.article`,
          //   link: retrieveDoc({ supportPage: `settingsFields`, language: user?.language.value }),
          // }}
          styles={{
            container: {
              padding: `16px 22px`,
              backgroundColor: RoutalPalette.white,
              borderBottom: `1px solid ${RoutalPalette.neutral10}`,
              maxHeight: `120px`,
            },
          }}
        />

        <Grid
          container
          direction="column"
          wrap="nowrap"
          style={{
            padding: `16px 22px`,
            overflowY: `auto`,
            paddingBottom: `22px`,
          }}
        >
          <Grid item container direction="column">
            <RoutalAvatarManager
              id="settings_account_profile_image_upload"
              shape="round"
              avatarUrl={user.avatar_url}
              fileName={generateAttachmentName(UPLOAD_TYPES.USER)}
              maxFileSize={MAX_AVATAR_IMAGE_SIZE}
              confirmText={intl.formatMessage({ id: `confirm` })}
              cancelText={intl.formatMessage({ id: `cancel` })}
              onRemoveAvatar={() => removeAvatar()}
              onAvatarUpload={(fileName: string, mimeType: string, file: Blob) =>
                uploadAvatar({ fileName, mimeType, file })
              }
              getUploadAsBase64={getUploadAsBase64}
            />
            <VSpacer medium />
          </Grid>

          <Grid item container direction="column">
            <Grid item>
              <InputText
                id="first_name"
                label={
                  <Text variant="label" style={{ marginBottom: `8px` }} intlId="settings.account.profile.inputname" />
                }
                fieldValue={firstName}
                onChange={(value: any) => setFirstName(value)}
                inputStyles={{ width: `300px` }}
              />
            </Grid>

            <VSpacer medium />

            <Grid item>
              <InputText
                id="last_name"
                label={
                  <Text
                    variant="label"
                    style={{ marginBottom: `8px` }}
                    intlId="settings.account.profile.inputlastname"
                  />
                }
                fieldValue={lastName}
                onChange={(value: any) => setLastName(value)}
                inputStyles={{ width: `300px` }}
              />
            </Grid>

            <VSpacer medium />

            <Grid item>
              <InputText
                id="email"
                label={
                  <Text variant="label" style={{ marginBottom: `8px` }} intlId="settings.account.profile.inputemail" />
                }
                fieldValue={email}
                onChange={(value: any) => setEmail(value)}
                inputStyles={{ width: `300px` }}
                disabled
              />
            </Grid>

            <VSpacer medium />

            <Grid item>
              <Text variant="label" intlId="settings.account.profile.inputphone" style={{ marginBottom: `8px` }} />

              <RoutalInputPhone
                id="phone"
                style={{
                  width: `316px`,
                }}
                countryCallingCodeEditable={false}
                focusInputOnCountrySelection
                international
                placeholder={intl.formatMessage({ id: `settings.account.profile.inputphone` })}
                defaultCountry={getCountryCode(language)}
                value={phone as RoutalInputPhoneValue}
                onChange={(value?: RoutalInputPhoneValue) => setPhone(value ?? ``)}
              />

              {!user.phone_validated ? (
                <>
                  <VSpacer small />
                  <Grid container direction="row">
                    <WarningIcon style={{ color: RoutalPalette.secondary40, fontSize: `16px`, marginRight: `4px` }} />
                    <Text intlId="settings.account.profile.inputphone.not_validated" />
                    <HSpacer small />
                    <Text
                      variant="button"
                      intlId="settings.account.profile.inputphone.validate"
                      onClick={() => {
                        setValidatePhoneDialogOptions({ open: true, fromTwoFactor: false });
                      }}
                    />
                  </Grid>
                </>
              ) : null}
            </Grid>

            <VSpacer medium />

            <Grid item>
              <InputLabel htmlFor="language-select">
                <Text intlId="settings.account.profile.inputlanguage" style={{ color: RoutalPalette.secondary00 }} />
              </InputLabel>

              <RoutalSelect
                id="language-select"
                style={{
                  padding: `4px 4px`,
                }}
                containerStyle={{
                  marginTop: `6px`,
                  maxWidth: `316px`,
                }}
                popoverProps={{
                  options: [
                    {
                      key: `en`,
                      width: 210,
                      content: <Text style={{ cursor: `pointer` }} intlId="settings.account.profile.en" />,
                      onClick: () => setLanguage(Language.create(`en`)),
                    },
                    {
                      key: `es`,
                      width: 210,
                      content: <Text style={{ cursor: `pointer` }} intlId="settings.account.profile.es" />,
                      onClick: () => setLanguage(Language.create(`es`)),
                    },
                    {
                      key: `ca`,
                      width: 210,
                      content: <Text style={{ cursor: `pointer` }} intlId="settings.account.profile.ca" />,
                      onClick: () => setLanguage(Language.create(`ca`)),
                    },
                    {
                      key: `pt`,
                      width: 210,
                      content: <Text style={{ cursor: `pointer` }} intlId="settings.account.profile.pt" />,
                      onClick: () => setLanguage(Language.create(`pt`)),
                    },
                    {
                      key: `de`,
                      width: 210,
                      content: <Text style={{ cursor: `pointer` }} intlId="settings.account.profile.de" />,
                      onClick: () => setLanguage(Language.create(`de`)),
                    },
                    {
                      key: `fr`,
                      width: 210,
                      content: <Text style={{ cursor: `pointer` }} intlId="settings.account.profile.fr" />,
                      onClick: () => setLanguage(Language.create(`fr`)),
                    },
                  ],
                }}
              >
                <Text
                  intlId={getLanguageIntlId(language)}
                  style={{ cursor: `pointer`, width: `100%`, paddingLeft: `8px`, textAlign: `start` }}
                />
              </RoutalSelect>
            </Grid>

            <VSpacer large />
          </Grid>

          <Grid item container direction="column">
            <Text variant="body-large" intlId="settings.account.profile.change_password" />
            <VSpacer medium />
            <BoxContainer>
              <Text variant="body" isBold intlId="settings.account.profile.changepassadvice" />
              <VSpacer small />
              <Button
                styleType="secondary"
                onClick={handleResetPassword}
                style={{ maxWidth: `200px` }}
                intlId="settings.account.profile.changepass"
              />
            </BoxContainer>
            <VSpacer large />
          </Grid>

          <Grid item container direction="column">
            <Text variant="body-large" intlId="settings.account.profile.notification" />
            <VSpacer medium />

            <BoxContainer display="flex" direction="column">
              <Grid display="flex" direction="row" alignItems="center">
                <Checkbox
                  id="notification-analytics_email"
                  checked={notifications.analytics_email}
                  onChange={(event) => {
                    const { checked } = event.target;
                    setNotifications({
                      ...notifications,
                      analytics_email: checked,
                    });
                  }}
                />
                <HSpacer small />
                <InputLabel htmlFor="notification-analytics_email">
                  <Text intlId="settings.account.profile.notification.analytics_email" />
                </InputLabel>
              </Grid>

              <Text
                variant="small"
                style={{ color: RoutalPalette.secondary00 }}
                intlId="settings.account.profile.notification.analytics_email.subtitle"
              />

              <Grid></Grid>
            </BoxContainer>

            <VSpacer large />
          </Grid>

          <Grid item container direction="column">
            <Text variant="body-large" intlId="settings.account.profile.2fa" />
            <VSpacer medium />

            <BoxContainer>
              <Grid display="flex" flexDirection="column">
                <Grid display="flex" flexDirection="row" alignItems="center">
                  <Checkbox
                    id="two_factor-email"
                    checked={twoFactor.email_enabled}
                    onChange={(event) => {
                      const { checked } = event.target;
                      setTwoFactor({
                        ...twoFactor,
                        email_enabled: checked,
                      });
                    }}
                    color="primary"
                  />
                  <HSpacer small />
                  <InputLabel htmlFor="two_factor-email">
                    <Text intlId="settings.account.profile.inputemail" />
                  </InputLabel>
                </Grid>

                <VSpacer small />

                <Text
                  variant="small"
                  style={{ color: RoutalPalette.secondary00 }}
                  intlId="settings.account.profile.inputemail.subtitle"
                />
              </Grid>

              <VSpacer medium />
              <Grid display="flex" flexDirection="column">
                <Grid display="flex" flexDirection="row" alignItems="center">
                  <Checkbox
                    id="two_factor-sms"
                    checked={twoFactor.sms_enabled}
                    onChange={(event) => {
                      if (!user.phone_validated) {
                        setValidatePhoneDialogOptions({ open: true, fromTwoFactor: true });
                        return;
                      }

                      const { checked } = event.target;
                      setTwoFactor({
                        ...twoFactor,
                        sms_enabled: checked,
                      });
                    }}
                    color="primary"
                  />
                  <HSpacer small />
                  <InputLabel htmlFor="two_factor-sms">
                    <Text intlId="settings.account.profile.sms" />
                  </InputLabel>
                </Grid>

                <VSpacer small />

                <Text
                  variant="small"
                  style={{ color: RoutalPalette.secondary00 }}
                  intlId="settings.account.profile.sms.subtitle"
                />
              </Grid>
            </BoxContainer>
          </Grid>

          <Grid item style={{ position: `fixed`, bottom: `40px`, right: `40px`, backgroundColor: `white` }}>
            <Button
              disabled={!dirtyState || isLoading}
              variant={dirtyState || isLoading ? `default` : `flat`}
              onClick={handleEditUser}
              style={{ maxWidth: `200px` }}
            >
              <Text
                variant="body"
                style={{
                  cursor: dirtyState && !isLoading ? `pointer` : `default`,
                  color: dirtyState || isLoading ? `white` : `inherit`,
                }}
                intlId="settings.buttonupdate"
              />
            </Button>
          </Grid>
        </Grid>
      </Grid>

      <ValidatePhoneDialog
        open={Boolean(validatePhoneDialogOptions)}
        onCloseDialog={(isValidated) => {
          if (isValidated === true) {
            const updatedTwoFactor: UserTwoFactor = {
              ...twoFactor,
              sms_enabled: true,
            };
            setTwoFactor(updatedTwoFactor);
            if (validatePhoneDialogOptions?.fromTwoFactor) {
              editUserProfile({ user, twoFactor: updatedTwoFactor });
            }
          }
          setValidatePhoneDialogOptions(undefined);
        }}
        title={<Text variant="h6" intlId="settings.account.profile.validatePhone-title" />}
        description={<Text variant="body" intlId="settings.account.profile.validatePhone-description" />}
      />
    </>
  );
};
